// src/pages/SecureDocumentAccessPage.tsx

import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';

/**
 * Öffentliche Seite für passwortgeschützten Kunden-Dokument-Zugriff
 * 
 * Route: /dokumente/:token (Plural - für Kunden)
 * Keine Authentifizierung erforderlich
 * 
 * Flow:
 * 1. Kunde gibt Passwort ein (aus Ansprechpartner-Stammdaten)
 * 2. Validierung via /.netlify/functions/secure-customer-access
 * 3. Bei Erfolg: Liste aller verfügbaren Uploads für diesen Ansprechpartner
 * 4. Download einzelner Dateien via /.netlify/functions/secure-customer-download
 * 
 * NEU: Jeder Ansprechpartner hat einen eigenen, isolierten Downloadbereich
 */

interface UploadFile {
  id: string;
  original_filename: string;
  file_size: number;
  file_type: string;
  sort_order: number;
  // Stufe 3.1: optionale Felder für xtax_portal-Pakete.
  // Werden von der secure-customer-access-Netlify-Function angereichert.
  // Bei manuellen Uploads (datev_document_type IS NULL) bleiben sie null.
  datev_document_type?: string | null;
  datev_document_titel?: string | null;
  datev_document_beschreibung?: string | null;
  // Stufe 3.1 (Gruppierung): Abschnitts-Zuordnung pro Datei.
  // Wird ebenfalls von secure-customer-access angereichert; bei manuellen
  // Uploads (keine Abschnitts-Zuordnung) null.
  datev_section_key?: string | null;
  datev_section_titel?: string | null;
}

interface Upload {
  id: string;
  titel: string;
  kommentar: string | null;
  versendet_am: string;
  created_at: string;
  files: UploadFile[];
  // Punkt 2 (Veralten-Tracking): gesetzt, sobald ein neueres Paket derselben
  // Gruppe versendet wurde. Kommt via select('*') aus der Netlify-Function.
  // Steuert die gedämpfte, eingeklappte Darstellung der Karte.
  veraltet_am?: string | null;
  // Folgepaket: Nachfolger eines veralteten Pakets bzw. Vorgänger und
  // Änderungen eines aktualisierten Pakets. Kommen ebenfalls via select('*').
  ersetzt_durch_upload_id?: string | null;
  folgepaket_von_upload_id?: string | null;
  folgepaket_aenderungen?: FolgepaketAenderungAnzeige[] | null;
}

interface FolgepaketAenderungAnzeige {
  dokument_typ: string;
  titel: string;
  art: 'geaendert' | 'neu';
  personalnummern?: number[];
  kassen?: string[];
}

interface CustomerInfo {
  id: string;
  company: string;
}

interface ContactInfo {
  id: string;
  displayName: string | null;
}

export const SecureDocumentAccessPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [passwort, setPasswort] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Nach erfolgreicher Authentifizierung
  const [authenticated, setAuthenticated] = useState(false);
  const [customer, setCustomer] = useState<CustomerInfo | null>(null);
  const [contact, setContact] = useState<ContactInfo | null>(null);
  const [uploads, setUploads] = useState<Upload[]>([]);
  
  // Download-Status pro Datei
  const [downloadingFiles, setDownloadingFiles] = useState<Set<string>>(new Set());
  const [downloadingAllUploadId, setDownloadingAllUploadId] = useState<string | null>(null);
  
  // Lösch-Status und Bestätigung
  const [deletingUploads, setDeletingUploads] = useState<Set<string>>(new Set());
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  // Punkt 2 (Veralten-Tracking): welche veralteten Pakete aufgeklappt sind.
  // Veraltete Pakete werden standardmäßig eingeklappt und gedämpft gezeigt;
  // ein Klick klappt den vollen Inhalt auf.
  const [expandierteVeraltete, setExpandierteVeraltete] = useState<Set<string>>(new Set());
  
  // Logout-Status
  const [loggedOut, setLoggedOut] = useState(false);
  
  // Passwort-Reset-Status
  const [resetLoading, setResetLoading] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);

  useEffect(() => {
    document.title = 'XTAX - Geschützter Dokumentenbereich';
    
    if (!token) {
      setError('Ungültiger Zugriffscode. Bitte überprüfen Sie den Link.');
    }
  }, [token]);

  /**
   * Formatiert ein Datum als deutschen String
   */
  const formatGermanDate = (dateString: string): string => {
    const date = new Date(dateString);
    const months = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];
    return `${date.getDate()}. ${months[date.getMonth()]} ${date.getFullYear()}`;
  };

  /**
   * Berechnet das Ablaufdatum (3 Jahre nach Upload)
   */
  const calculateExpiryDate = (createdAt: string): string => {
    const date = new Date(createdAt);
    date.setFullYear(date.getFullYear() + 3);
    return formatGermanDate(date.toISOString());
  };

  /**
   * Folgepaket: eine Zeile der Änderungsliste, z. B.
   * "Lohnabrechnungen: Personalnummern 7, 425".
   */
  const beschreibeAenderung = (aenderung: FolgepaketAenderungAnzeige): string => {
    let zeile = `${aenderung.titel || aenderung.dokument_typ}${aenderung.art === 'neu' ? ' (neu)' : ''}`;
    if (aenderung.personalnummern && aenderung.personalnummern.length > 0) {
      zeile += `: Personalnummern ${aenderung.personalnummern.join(', ')}`;
    }
    if (aenderung.kassen && aenderung.kassen.length > 0) {
      zeile += `: ${aenderung.kassen.join(', ')}`;
    }
    return zeile;
  };

  /**
   * Punkt 2 (Veralten-Tracking): veraltetes Paket auf-/zuklappen.
   */
  const toggleVeraltet = (uploadId: string) => {
    setExpandierteVeraltete(prev => {
      const next = new Set(prev);
      if (next.has(uploadId)) {
        next.delete(uploadId);
      } else {
        next.add(uploadId);
      }
      return next;
    });
  };

  /**
   * Formatiert Dateigröße
   */
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  /**
   * Passwort-Authentifizierung
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!token) {
      setError('Ungültiger Zugriffscode.');
      return;
    }

    if (!passwort.trim()) {
      setError('Bitte geben Sie Ihr Passwort ein.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Zugriffscode und Passwort im JSON-Inhalt einer POST-Anfrage, damit das
      // Passwort nicht in der Adresse steht.
      const response = await fetch('/.netlify/functions/secure-customer-access', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, passwort })
      });

      const result = await response.json();

      if (!response.ok || !result.valid) {
        setError(result.error || 'Ungültiges Passwort oder Zugriffscode.');
        setLoading(false);
        return;
      }

      // Erfolg - Daten speichern
      setCustomer(result.customer);
      setContact(result.contact || null);
      setUploads(result.uploads || []);
      setAuthenticated(true);
      setLoading(false);

    } catch (err) {
      console.error('Secure document access error:', err);
      setError('Ein technischer Fehler ist aufgetreten. Bitte versuchen Sie es später erneut.');
      setLoading(false);
    }
  };

  /**
   * Download einer einzelnen Datei
   */
  const handleDownload = async (fileId: string, filename: string) => {
    if (!token || downloadingFiles.has(fileId)) return;

    setDownloadingFiles(prev => new Set(prev).add(fileId));

    try {
      // Zugangsdaten und Datei-ID im JSON-Inhalt einer POST-Anfrage, damit das
      // Passwort nicht in der Adresse steht.
      const response = await fetch('/.netlify/functions/secure-customer-download', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, passwort, file_id: fileId })
      });

      const result = await response.json();

      if (!response.ok || !result.valid) {
        setError(result.error || 'Download fehlgeschlagen.');
        return;
      }

      // Download starten
      if (result.downloadUrl) {
        const a = document.createElement('a');
        a.href = result.downloadUrl;
        a.download = filename;
        a.style.display = 'none';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }

    } catch (err) {
      console.error('Download error:', err);
      setError('Download fehlgeschlagen. Bitte versuchen Sie es erneut.');
    } finally {
      setDownloadingFiles(prev => {
        const next = new Set(prev);
        next.delete(fileId);
        return next;
      });
    }
  };

  /**
   * Download aller Dateien eines Uploads (sequenziell mit Delay)
   */
  const handleDownloadAll = async (upload: Upload) => {
    if (!token || downloadingAllUploadId) return;

    setDownloadingAllUploadId(upload.id);
    setError(null);

    const sortedFiles = [...upload.files].sort((a, b) => {
      // Stufe 3.1: Sort nach sort_order (semantische Reihenfolge bei
      // xtax_portal-Paketen); Filename als Tiebreaker bei gleichem sort_order
      const orderDiff = (a.sort_order ?? 0) - (b.sort_order ?? 0);
      if (orderDiff !== 0) return orderDiff;
      return a.original_filename.localeCompare(b.original_filename, 'de');
    });

    for (let i = 0; i < sortedFiles.length; i++) {
      const file = sortedFiles[i];
      try {
        setDownloadingFiles(prev => new Set(prev).add(file.id));

        // Zugangsdaten und Datei-ID im JSON-Inhalt einer POST-Anfrage, damit das
        // Passwort nicht in der Adresse steht.
        const response = await fetch('/.netlify/functions/secure-customer-download', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token, passwort, file_id: file.id })
        });

        const result = await response.json();

        if (response.ok && result.valid && result.downloadUrl) {
          const a = document.createElement('a');
          a.href = result.downloadUrl;
          a.download = file.original_filename;
          a.style.display = 'none';
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        }
      } catch (err) {
        console.error(`Download error for ${file.original_filename}:`, err);
      } finally {
        setDownloadingFiles(prev => {
          const next = new Set(prev);
          next.delete(file.id);
          return next;
        });
      }

      // Kurzer Delay zwischen Downloads damit der Browser nicht blockiert
      if (i < sortedFiles.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 500));
      }
    }

    setDownloadingAllUploadId(null);
  };

  /**
   * Logout - State clearen und Bestätigungsseite zeigen
   */
  const handleLogout = () => {
    // State komplett zurücksetzen
    setAuthenticated(false);
    setCustomer(null);
    setContact(null);
    setUploads([]);
    setPasswort('');
    setError(null);
    setConfirmDeleteId(null);
    
    // Browser-Verlauf ersetzen, damit "Zurück" nicht funktioniert
    window.history.replaceState(null, '', window.location.pathname);
    
    // Logout-Screen anzeigen
    setLoggedOut(true);
  };

  /**
   * Löschen eines Uploads
   */
  const handleDeleteUpload = async (uploadId: string) => {
    if (!token || deletingUploads.has(uploadId)) return;

    setDeletingUploads(prev => new Set(prev).add(uploadId));
    setError(null);

    try {
      // Zugangsdaten und Paket-ID im JSON-Inhalt einer POST-Anfrage, damit das
      // Passwort nicht in der Adresse steht.
      const response = await fetch('/.netlify/functions/secure-customer-delete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, passwort, upload_id: uploadId })
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        setError(result.error || 'Löschen fehlgeschlagen.');
        return;
      }

      // Upload aus Liste entfernen
      setUploads(prev => prev.filter(u => u.id !== uploadId));
      setConfirmDeleteId(null);

    } catch (err) {
      console.error('Delete error:', err);
      setError('Löschen fehlgeschlagen. Bitte versuchen Sie es erneut.');
    } finally {
      setDeletingUploads(prev => {
        const next = new Set(prev);
        next.delete(uploadId);
        return next;
      });
    }
  };

  /**
   * Passwort-Reset anfordern via Self-Service
   * Sendet E-Mail mit Setup-Link an hinterlegte E-Mail-Adresse
   */
  const handlePasswordReset = async () => {
    if (!token || resetLoading) return;

    setResetLoading(true);
    setError(null);
    setResetSuccess(false);

    try {
      const response = await fetch('/.netlify/functions/secure-customer-request-password-setup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ download_token: token })
      });

      const result = await response.json();

      if (result.success) {
        setResetSuccess(true);
      }

    } catch (err) {
      console.error('Password reset request error:', err);
      // Auch bei Fehlern neutrale Erfolgsmeldung anzeigen (kein Information Leaking)
      setResetSuccess(true);
    } finally {
      setResetLoading(false);
    }
  };

  // ============================================
  // SHARED: Security Trust Badges
  // ============================================
  const SecurityBadges = ({ compact = false }: { compact?: boolean }) => (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      gap: compact ? '12px' : '20px',
      flexWrap: 'wrap',
      marginTop: compact ? '16px' : '24px',
      marginBottom: compact ? '0' : '8px'
    }}>
      {/* SSL Badge */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        fontSize: compact ? '11px' : '12px',
        color: '#059669',
        backgroundColor: '#ecfdf5',
        padding: compact ? '4px 8px' : '6px 12px',
        borderRadius: '4px',
        border: '1px solid #a7f3d0'
      }}>
        <svg width={compact ? "12" : "14"} height={compact ? "12" : "14"} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
          <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
        </svg>
        SSL-verschlüsselt
      </div>

      {/* DSGVO Badge */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        fontSize: compact ? '11px' : '12px',
        color: '#2563eb',
        backgroundColor: '#eff6ff',
        padding: compact ? '4px 8px' : '6px 12px',
        borderRadius: '4px',
        border: '1px solid #bfdbfe'
      }}>
        <svg width={compact ? "12" : "14"} height={compact ? "12" : "14"} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
          <path d="M9 12l2 2 4-4"/>
        </svg>
        DSGVO-konform
      </div>

      {/* Server Location Badge */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        fontSize: compact ? '11px' : '12px',
        color: '#374151',
        backgroundColor: '#f3f4f6',
        padding: compact ? '4px 8px' : '6px 12px',
        borderRadius: '4px',
        border: '1px solid #d1d5db'
      }}>
        <span style={{ fontSize: compact ? '10px' : '12px' }}>🇩🇪</span>
        Serverstandort: Frankfurt am Main
      </div>
    </div>
  );

  // ============================================
  // SHARED: Datei-Liste eines Uploads rendern
  // ============================================
  // Bei xtax_portal-Paketen (mindestens eine Datei mit datev_section_titel)
  // werden die Dateien nach Abschnitt gruppiert und je Abschnitt eine
  // Überschrift eingezogen. Dateien ohne Abschnitt (manuelle Anhänge) landen
  // in einem abschließenden Block "Weitere Dokumente". Hat ein Paket gar
  // keine Abschnitts-Zuordnung (reine manuelle Uploads), bleibt die Liste
  // flach und unverändert wie bisher. Die Sortierung bleibt in allen Fällen
  // sort_order ASC mit Filename-Tiebreaker.
  const renderUploadFiles = (upload: Upload) => {
    const sortedFiles = [...upload.files].sort((a, b) => {
      const orderDiff = (a.sort_order ?? 0) - (b.sort_order ?? 0);
      if (orderDiff !== 0) return orderDiff;
      return a.original_filename.localeCompare(b.original_filename, 'de');
    });

    const hasSections = sortedFiles.some(f => !!f.datev_section_titel);

    let lastSectionKey: string | null = null;
    let headerCount = 0;

    return sortedFiles.map((file) => {
      let sectionHeader: React.ReactNode = null;

      if (hasSections) {
        const effectiveKey = file.datev_section_titel || '__weitere__';
        if (effectiveKey !== lastSectionKey) {
          lastSectionKey = effectiveKey;
          const isFirst = headerCount === 0;
          headerCount++;
          const headerLabel = file.datev_section_titel || 'Weitere Dokumente';
          sectionHeader = (
            <div style={{
              fontSize: '13px',
              fontWeight: '600',
              color: '#374151',
              textTransform: 'uppercase',
              letterSpacing: '0.03em',
              margin: isFirst ? '0 0 10px 0' : '20px 0 10px 0'
            }}>
              {headerLabel}
            </div>
          );
        }
      }

      return (
        <React.Fragment key={file.id}>
          {sectionHeader}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '14px',
              backgroundColor: '#f9fafb',
              borderRadius: '8px',
              marginBottom: '8px',
              border: '1px solid #e5e7eb'
            }}
          >
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              overflow: 'hidden',
              flex: 1
            }}>
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '8px',
                backgroundColor: file.file_type === 'pdf' ? '#fef2f2' : 
                                 file.file_type === 'xlsx' || file.file_type === 'xls' || file.file_type === 'csv' ? '#ecfdf5' :
                                 file.file_type === 'doc' || file.file_type === 'docx' ? '#eff6ff' :
                                 '#f3f4f6',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                <span style={{ fontSize: '20px' }}>
                  {file.file_type === 'pdf' ? '📄' : 
                   file.file_type === 'xlsx' || file.file_type === 'xls' || file.file_type === 'csv' ? '📊' :
                   file.file_type === 'doc' || file.file_type === 'docx' ? '📝' :
                   file.file_type === 'jpg' || file.file_type === 'jpeg' || file.file_type === 'png' ? '🖼️' :
                   file.file_type === 'zip' ? '📦' : '📎'}
                </span>
              </div>
              <div style={{ overflow: 'hidden', flex: 1 }}>
                <p style={{
                  margin: '0',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#111827',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}>
                  {file.original_filename}
                </p>
                {/* Stufe 3.1: DATEV-Dokumententyp-Beschreibung für xtax_portal-Files */}
                {file.datev_document_titel && (
                  <p style={{
                    margin: '4px 0 0 0',
                    fontSize: '13px',
                    fontWeight: '500',
                    color: '#374151',
                    lineHeight: '1.4'
                  }}>
                    {file.datev_document_titel}
                  </p>
                )}
                {file.datev_document_beschreibung && (
                  <p style={{
                    margin: '2px 0 0 0',
                    fontSize: '12px',
                    color: '#6b7280',
                    lineHeight: '1.4',
                    whiteSpace: 'pre-line'
                  }}>
                    {file.datev_document_beschreibung}
                  </p>
                )}
                <p style={{
                  margin: '2px 0 0 0',
                  fontSize: '12px',
                  color: '#6b7280'
                }}>
                  {formatFileSize(file.file_size)} • {file.file_type.toUpperCase()}
                </p>
              </div>
            </div>
            <button
              onClick={() => handleDownload(file.id, file.original_filename)}
              disabled={downloadingFiles.has(file.id)}
              style={{
                padding: '10px 20px',
                backgroundColor: downloadingFiles.has(file.id) ? '#9ca3af' : '#3465de',
                color: 'white',
                fontSize: '14px',
                fontWeight: '500',
                border: 'none',
                borderRadius: '8px',
                cursor: downloadingFiles.has(file.id) ? 'not-allowed' : 'pointer',
                transition: 'background-color 0.2s',
                whiteSpace: 'nowrap',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                marginLeft: '12px',
                flexShrink: 0
              }}
              onMouseEnter={(e) => {
                if (!downloadingFiles.has(file.id)) {
                  e.currentTarget.style.backgroundColor = '#2952b3';
                }
              }}
              onMouseLeave={(e) => {
                if (!downloadingFiles.has(file.id)) {
                  e.currentTarget.style.backgroundColor = '#3465de';
                }
              }}
            >
              {downloadingFiles.has(file.id) ? (
                <>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ animation: 'spin 1s linear infinite' }}>
                    <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
                  </svg>
                  Lädt...
                </>
              ) : (
                <>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                    <polyline points="7 10 12 15 17 10"/>
                    <line x1="12" y1="15" x2="12" y2="3"/>
                  </svg>
                  Download
                </>
              )}
            </button>
          </div>
        </React.Fragment>
      );
    });
  };

  // ============================================
  // RENDER: Logout-Bestätigung
  // ============================================
  if (loggedOut) {
    return (
      <div style={{
        backgroundColor: '#f9fafb',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}>
        <div style={{
          width: '100%',
          maxWidth: '450px',
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
          padding: '40px',
          textAlign: 'center'
        }}>
          {/* Success Icon */}
          <div style={{
            width: '64px',
            height: '64px',
            borderRadius: '50%',
            backgroundColor: '#ecfdf5',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px'
          }}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2">
              <path d="M20 6L9 17l-5-5"/>
            </svg>
          </div>
          
          <h1 style={{
            fontSize: '22px',
            fontWeight: '600',
            color: '#111827',
            margin: '0 0 12px 0'
          }}>
            Erfolgreich abgemeldet
          </h1>
          
          <p style={{
            fontSize: '14px',
            color: '#6b7280',
            margin: '0 0 32px 0',
            lineHeight: '1.6'
          }}>
            Sie wurden sicher aus dem geschützten Dokumentenbereich abgemeldet. 
            Ihr Browserfenster kann nun geschlossen werden.
          </p>
          
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px'
          }}>
            <button
              onClick={() => setLoggedOut(false)}
              style={{
                padding: '12px 24px',
                backgroundColor: '#3465de',
                color: 'white',
                fontSize: '14px',
                fontWeight: '500',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#2952b3'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#3465de'}
            >
              Erneut anmelden
            </button>
            
            <a
              href="https://www.xtax.de"
              style={{
                padding: '12px 24px',
                backgroundColor: '#f3f4f6',
                color: '#374151',
                fontSize: '14px',
                fontWeight: '500',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                textDecoration: 'none',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e5e7eb'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
            >
              Zur XTAX Website →
            </a>
          </div>
        </div>
        
      </div>
    );
  }

  // ============================================
  // RENDER: Passwort-Eingabe (Phase 1)
  // ============================================
  if (!authenticated) {
    return (
      <div style={{
        backgroundColor: '#f9fafb',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '20px'
      }}>
        {/* Header */}
        <div style={{
          width: '100%',
          maxWidth: '500px',
          textAlign: 'center',
          marginBottom: '24px',
          marginTop: '40px'
        }}>
          <h1 style={{
            fontSize: '24px',
            fontWeight: '600',
            color: '#111827',
            margin: '0 0 8px 0'
          }}>
            Geschützter Dokumentenbereich
          </h1>
          <p style={{
            fontSize: '14px',
            color: '#6b7280',
            margin: '0'
          }}>
            Sicherer Zugriff auf Ihre Dokumente
          </p>
        </div>

        {/* Security Badges */}
        <SecurityBadges />

        {/* Hauptinhalt */}
        <div style={{
          width: '100%',
          maxWidth: '500px',
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
          padding: '32px',
          marginTop: '24px'
        }}>
          {/* Info-Box */}
          <div style={{
            backgroundColor: '#f0f7ff',
            border: '1px solid #bfdbfe',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '24px'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '12px'
            }}>
              <div style={{
                backgroundColor: '#3465de',
                borderRadius: '50%',
                width: '36px',
                height: '36px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
              </div>
              <div>
                <p style={{
                  margin: '0 0 4px 0',
                  fontWeight: '600',
                  color: '#1e40af',
                  fontSize: '15px'
                }}>
                  Passwortgeschützter Bereich
                </p>
                <p style={{
                  margin: '0',
                  fontSize: '14px',
                  color: '#374151',
                  lineHeight: '1.5'
                }}>
                  Bitte geben Sie Ihr <strong>persönliches Passwort</strong> ein, 
                  um auf Ihre Dokumente zugreifen zu können.
                </p>
              </div>
            </div>
          </div>

          {/* Formular */}
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '8px'
              }}>
                Passwort
              </label>
              <input
                type="password"
                value={passwort}
                onChange={(e) => setPasswort(e.target.value)}
                placeholder="Ihr Passwort"
                disabled={loading || !token}
                autoComplete="current-password"
                style={{
                  width: '100%',
                  padding: '14px',
                  fontSize: '16px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  outline: 'none',
                  boxSizing: 'border-box',
                  transition: 'border-color 0.2s, box-shadow 0.2s'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#3465de';
                  e.target.style.boxShadow = '0 0 0 3px rgba(52, 101, 222, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#d1d5db';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </div>

            {/* Error Message */}
            {error && (
              <div style={{
                backgroundColor: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: '8px',
                padding: '12px',
                marginBottom: '20px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="12" y1="8" x2="12" y2="12"/>
                  <line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
                <p style={{
                  margin: '0',
                  fontSize: '14px',
                  color: '#991b1b'
                }}>
                  {error}
                </p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading || !token}
              style={{
                width: '100%',
                padding: '14px',
                backgroundColor: loading ? '#9ca3af' : '#3465de',
                color: 'white',
                fontSize: '16px',
                fontWeight: '600',
                border: 'none',
                borderRadius: '8px',
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'background-color 0.2s',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
              onMouseEnter={(e) => {
                if (!loading) e.currentTarget.style.backgroundColor = '#2952b3';
              }}
              onMouseLeave={(e) => {
                if (!loading) e.currentTarget.style.backgroundColor = '#3465de';
              }}
            >
              {loading ? (
                <>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ animation: 'spin 1s linear infinite' }}>
                    <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
                  </svg>
                  Wird überprüft...
                </>
              ) : (
                <>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                    <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                  </svg>
                  Zugang freischalten
                </>
              )}
            </button>
          </form>

          {/* Passwort vergessen */}
          <div style={{
            marginTop: '20px',
            textAlign: 'center'
          }}>
            {resetSuccess ? (
              <div style={{
                backgroundColor: '#ecfdf5',
                border: '1px solid #a7f3d0',
                borderRadius: '8px',
                padding: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                justifyContent: 'center'
              }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2">
                  <path d="M9 12l2 2 4-4"/>
                  <circle cx="12" cy="12" r="10"/>
                </svg>
                <p style={{
                  margin: '0',
                  fontSize: '13px',
                  color: '#065f46'
                }}>
                  Falls eine E-Mail-Adresse hinterlegt ist, erhalten Sie in Kürze eine E-Mail mit einem Link zur Passwort-Einrichtung.
                </p>
              </div>
            ) : (
              <button
                onClick={handlePasswordReset}
                disabled={resetLoading}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: '0',
                  fontSize: '13px',
                  color: resetLoading ? '#9ca3af' : '#3465de',
                  cursor: resetLoading ? 'not-allowed' : 'pointer',
                  textDecoration: 'none',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
              >
                {resetLoading ? (
                  <>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ animation: 'spin 1s linear infinite' }}>
                      <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
                    </svg>
                    Wird verarbeitet...
                  </>
                ) : (
                  'Passwort vergessen?'
                )}
              </button>
            )}
          </div>
        </div>


        {/* Spinner Animation */}
        <style>
          {`
            @keyframes spin {
              from { transform: rotate(0deg); }
              to { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    );
  }

  // ============================================
  // RENDER: Dokument-Liste (Phase 2)
  // ============================================
  return (
    <div style={{
      backgroundColor: '#f9fafb',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      padding: '20px'
    }}>
      {/* Header */}
      <div style={{
        width: '100%',
        maxWidth: '700px',
        marginBottom: '16px',
        marginTop: '30px'
      }}>
        <div style={{ textAlign: 'center' }}>
          {/* Firmenname mit Logout-Link */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px',
            marginBottom: '8px'
          }}>
            <h1 style={{
              fontSize: '24px',
              fontWeight: '600',
              color: '#111827',
              margin: '0'
            }}>
              {customer?.company}
              {contact?.displayName && (
                <span style={{ fontWeight: '400', color: '#6b7280', fontSize: '18px' }}>
                  {' '}— {contact.displayName}
                </span>
              )}
            </h1>
            <span style={{ color: '#d1d5db' }}>|</span>
            <button
              onClick={handleLogout}
              style={{
                background: 'none',
                border: 'none',
                color: '#6b7280',
                fontSize: '14px',
                cursor: 'pointer',
                padding: '4px 8px',
                borderRadius: '4px',
                transition: 'all 0.2s',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = '#374151';
                e.currentTarget.style.backgroundColor = '#f3f4f6';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = '#6b7280';
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                <polyline points="16 17 21 12 16 7"/>
                <line x1="21" y1="12" x2="9" y2="12"/>
              </svg>
              Abmelden
            </button>
          </div>
          
        <p style={{
          fontSize: '14px',
          color: '#6b7280',
          margin: '0'
        }}>
          {uploads.length} {uploads.length === 1 ? 'Dokumentenpaket' : 'Dokumentenpakete'} verfügbar
        </p>
        
        {/* Compact Security Badges */}
        <SecurityBadges compact />
        </div>
      </div>

      {/* Fehleranzeige */}
      {error && (
        <div style={{
          width: '100%',
          maxWidth: '700px',
          backgroundColor: '#fef2f2',
          border: '1px solid #fca5a5',
          borderRadius: '8px',
          padding: '12px',
          marginBottom: '20px'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <line x1="12" y1="8" x2="12" y2="12"/>
              <line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
            <p style={{
              margin: '0',
              fontSize: '14px',
              color: '#991b1b'
            }}>
              {error}
            </p>
          </div>
        </div>
      )}

      {/* Upload-Liste */}
      {uploads.map((upload) => {
        // Punkt 2 (Veralten-Tracking): veraltete Pakete werden gedämpft und
        // standardmäßig eingeklappt dargestellt (Hinweis „veraltete Fassung"
        // an der Datei-Zeile). Aktive Pakete bleiben exakt wie bisher. Die
        // chronologische Position bleibt in beiden Fällen erhalten.
        const istVeraltet = !!upload.veraltet_am;
        const istAufgeklappt = expandierteVeraltete.has(upload.id);
        const zeigeInhalt = !istVeraltet || istAufgeklappt;
        // Folgepaket: Nachfolger des veralteten bzw. Vorgänger des
        // aktualisierten Pakets aus derselben Liste.
        const nachfolger = istVeraltet && upload.ersetzt_durch_upload_id
          ? uploads.find(u => u.id === upload.ersetzt_durch_upload_id)
          : undefined;
        const vorgaenger = upload.folgepaket_von_upload_id
          ? uploads.find(u => u.id === upload.folgepaket_von_upload_id)
          : undefined;
        const aenderungen = upload.folgepaket_aenderungen ?? [];
        return (
        <div
          key={upload.id}
          style={{
            width: '100%',
            maxWidth: '700px',
            backgroundColor: 'white',
            borderRadius: '12px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            padding: '24px',
            marginBottom: '16px',
            opacity: istVeraltet ? 0.6 : 1
          }}
        >
          {istVeraltet ? (
            /* Veraltete Fassung: identisch zum aktiven Header, nur mit dezentem
               Hinweis „veraltete Fassung" an der Datei-Zeile und Auf-/Zuklappen
               statt Verfügbarkeits-Marke. Die ganze Karte tritt via opacity
               zurück; die chronologische Position bleibt unberührt. */
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              marginBottom: zeigeInhalt ? '16px' : '0',
              flexWrap: 'wrap',
              gap: '12px'
            }}>
              <div>
                <h2 style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: '#111827',
                  margin: '0 0 4px 0'
                }}>
                  {upload.titel}
                </h2>
                <p style={{
                  fontSize: '13px',
                  color: '#6b7280',
                  margin: '0'
                }}>
                  {upload.files.length} {upload.files.length === 1 ? 'Datei' : 'Dateien'} · veraltete Fassung
                  {nachfolger && ` · ersetzt durch das Paket vom ${formatGermanDate(nachfolger.versendet_am || nachfolger.created_at)}`}
                </p>
              </div>
              <button
                onClick={() => toggleVeraltet(upload.id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '8px 12px',
                  backgroundColor: 'transparent',
                  color: '#6b7280',
                  fontSize: '13px',
                  fontWeight: '500',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f3f4f6'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
              >
                {istAufgeklappt ? 'Ausblenden' : 'Dokumente anzeigen'}
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ transform: istAufgeklappt ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>
                  <polyline points="6 9 12 15 18 9"/>
                </svg>
              </button>
            </div>
          ) : (
          /* Upload Header */
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: '16px',
            flexWrap: 'wrap',
            gap: '12px'
          }}>
            <div>
              <h2 style={{
                fontSize: '16px',
                fontWeight: '600',
                color: '#111827',
                margin: '0 0 4px 0'
              }}>
                {upload.titel}
              </h2>
              <p style={{
                fontSize: '13px',
                color: '#6b7280',
                margin: '0'
              }}>
                {upload.files.length} {upload.files.length === 1 ? 'Datei' : 'Dateien'}
              </p>
            </div>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              backgroundColor: '#ecfdf5',
              padding: '6px 12px',
              borderRadius: '6px',
              fontSize: '12px',
              color: '#059669',
              border: '1px solid #a7f3d0',
              whiteSpace: 'nowrap'
            }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <polyline points="12 6 12 12 16 14"/>
              </svg>
              Verfügbar bis {calculateExpiryDate(upload.created_at)}
            </div>
          </div>
          )}

          {zeigeInhalt && (
          <>
          {/* Folgepaket: Hinweis auf das ersetzte Paket und die Änderungen */}
          {upload.folgepaket_von_upload_id && (
            <div style={{
              backgroundColor: '#eff6ff',
              border: '1px solid #bfdbfe',
              padding: '12px 16px',
              marginBottom: '16px',
              fontSize: '14px',
              color: '#1e3a8a',
              lineHeight: '1.5',
              borderRadius: '8px'
            }}>
              <div style={{ fontWeight: '600', marginBottom: '4px' }}>
                Aktualisierte Fassung
              </div>
              <div>
                {vorgaenger
                  ? `Dieses Paket ersetzt das Paket vom ${formatGermanDate(vorgaenger.versendet_am || vorgaenger.created_at)}.`
                  : 'Dieses Paket ersetzt ein zuvor bereitgestelltes Paket.'}
                {' Bitte verwenden Sie nur noch diese Unterlagen.'}
              </div>
              {aenderungen.length > 0 && (
                <>
                  <div style={{ marginTop: '8px' }}>Geändert haben sich:</div>
                  <ul style={{ margin: '4px 0 0 0', paddingLeft: '20px' }}>
                    {aenderungen.map((aenderung, index) => (
                      <li key={index}>{beschreibeAenderung(aenderung)}</li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          )}

          {/* Kommentar */}
          {upload.kommentar && (
            <div style={{
              backgroundColor: '#f9fafb',
              borderLeft: '3px solid #3465de',
              padding: '12px 16px',
              marginBottom: '16px',
              fontSize: '14px',
              color: '#374151',
              lineHeight: '1.5',
              borderRadius: '0 8px 8px 0',
              whiteSpace: 'pre-line'
            }}>
              {upload.kommentar}
            </div>
          )}

          {/* Datei-Liste */}
          <div style={{
            borderTop: '1px solid #e5e7eb',
            paddingTop: '16px'
          }}>
            {renderUploadFiles(upload)}
          </div>

          {/* Alle herunterladen - nur bei mehr als einer Datei */}
          {upload.files.length > 1 && (
            <div style={{
              borderTop: '1px solid #e5e7eb',
              paddingTop: '16px',
              marginTop: '8px'
            }}>
              <button
                onClick={() => handleDownloadAll(upload)}
                disabled={downloadingAllUploadId === upload.id}
                style={{
                  width: '100%',
                  padding: '12px 20px',
                  backgroundColor: downloadingAllUploadId === upload.id ? '#9ca3af' : '#3465de',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: '500',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: downloadingAllUploadId === upload.id ? 'not-allowed' : 'pointer',
                  transition: 'background-color 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
                onMouseEnter={(e) => {
                  if (downloadingAllUploadId !== upload.id) {
                    e.currentTarget.style.backgroundColor = '#2952b3';
                  }
                }}
                onMouseLeave={(e) => {
                  if (downloadingAllUploadId !== upload.id) {
                    e.currentTarget.style.backgroundColor = '#3465de';
                  }
                }}
              >
                {downloadingAllUploadId === upload.id ? (
                  <>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ animation: 'spin 1s linear infinite' }}>
                      <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
                    </svg>
                    Dateien werden heruntergeladen...
                  </>
                ) : (
                  <>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                      <polyline points="7 10 12 15 17 10"/>
                      <line x1="12" y1="15" x2="12" y2="3"/>
                    </svg>
                    Alle {upload.files.length} Dateien herunterladen
                  </>
                )}
              </button>
            </div>
          )}

          {/* Lösch-Option */}
          <div style={{
            borderTop: '1px solid #e5e7eb',
            paddingTop: '16px',
            marginTop: '8px'
          }}>
            {confirmDeleteId === upload.id ? (
              <div style={{
                backgroundColor: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: '8px',
                padding: '16px'
              }}>
                <p style={{
                  margin: '0 0 12px 0',
                  fontSize: '14px',
                  color: '#991b1b',
                  fontWeight: '500'
                }}>
                  Möchten Sie dieses Dokumentenpaket wirklich unwiderruflich löschen?
                </p>
                <div style={{
                  display: 'flex',
                  gap: '12px',
                  justifyContent: 'flex-end'
                }}>
                  <button
                    onClick={() => setConfirmDeleteId(null)}
                    disabled={deletingUploads.has(upload.id)}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#f3f4f6',
                      color: '#374151',
                      fontSize: '14px',
                      fontWeight: '500',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      cursor: 'pointer'
                    }}
                  >
                    Abbrechen
                  </button>
                  <button
                    onClick={() => handleDeleteUpload(upload.id)}
                    disabled={deletingUploads.has(upload.id)}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: deletingUploads.has(upload.id) ? '#9ca3af' : '#dc2626',
                      color: 'white',
                      fontSize: '14px',
                      fontWeight: '500',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: deletingUploads.has(upload.id) ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                  >
                    {deletingUploads.has(upload.id) ? (
                      <>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ animation: 'spin 1s linear infinite' }}>
                          <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
                        </svg>
                        Wird gelöscht...
                      </>
                    ) : (
                      'Endgültig löschen'
                    )}
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => setConfirmDeleteId(upload.id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '8px 12px',
                  backgroundColor: 'transparent',
                  color: '#6b7280',
                  fontSize: '13px',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#fef2f2';
                  e.currentTarget.style.color = '#dc2626';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = '#6b7280';
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="3 6 5 6 21 6"/>
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                  <line x1="10" y1="11" x2="10" y2="17"/>
                  <line x1="14" y1="11" x2="14" y2="17"/>
                </svg>
                Dokumentenpaket löschen
              </button>
            )}
          </div>
          </>
          )}
        </div>
        );
      })}


      {/* Spinner Animation */}
      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};
